/**
 * Finish the old plans, then end them.
 *
 * The owner, Sep 21 2026: "the big 8 are the 8 that were already on a plan. I
 * want them to finish their plan and then end it March. What they have left is
 * 2 ferts, 1 pre, 1 post, for the basic guys — and then the others have an
 * aeration and a winterizer and 1 insect control."
 *
 * These people bought a year, not a subscription. This is not more service: it
 * is what is still owed on what they already paid for, laid out between now and
 * March, and then the plan stops and the monthly billing stops with it.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';

type Kind = 'basic' | 'more' | 'skip';
const CHOICES: { key: Kind; label: string }[] = [
  { key: 'more', label: 'Plus aeration' },
  { key: 'basic', label: 'Basic' },
  { key: 'skip', label: 'Leave alone' },
];
const money = (n: any) => `$${Math.round(Number(n) || 0)}`;
const day = (iso?: string | null) =>
  (iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'no mow booked yet');

export default function PlanFinish() {
  const qc = useQueryClient();
  const [kinds, setKinds] = useState<Record<string, Kind>>({});
  const [done, setDone] = useState<any>(null);

  const key = JSON.stringify(kinds);
  const { data, isLoading, isError } = useQuery(['plan-finish', key],
    () => api.get(`/treatments/finish${key === '{}' ? '' : `?kinds=${encodeURIComponent(key)}`}`).then(r => r.data),
    { keepPreviousData: true });
  const people: any[] = data?.people || [];

  useEffect(() => {
    const list: any[] = data?.people || [];
    if (!list.length) return;
    setKinds(k => (Object.keys(k).length ? k : Object.fromEntries(list.map(p => [String(p.program_id), (p.kind || 'more') as Kind]))));
  }, [data]);

  const pick = (id: number) => (kinds[String(id)] || 'more') as Kind;
  const taking = people.filter(p => pick(p.program_id) !== 'skip');

  const finish = useMutation(
    () => api.post('/treatments/finish', {
      kinds: Object.fromEntries(people.map(p => [p.program_id, pick(p.program_id)])),
      end_month: data?.end_month,
    }).then(r => r.data),
    {
      onSuccess: (d: any) => {
        setDone(d); toast.success(d?.message || 'Set to finish', { autoClose: 9000 });
        qc.invalidateQueries('plan-finish'); qc.invalidateQueries('treatment-programs');
      },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not set those to finish'); },
    });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (isError) return <p className="text-sm text-amber-700">Could not read those plans.</p>;
  if (!people.length) return <div className="card p-6 text-center text-sm text-gray-500">Nobody is on a lawn plan right now.</div>;

  return (
    <div className="space-y-3">
      <div className="card p-4 space-y-2">
        <p className="text-sm text-gray-800">
          These people bought a <b>year</b>, not a subscription. This books what is still owed on it and then ends the
          plan — it does not add anything, and it does not change what they pay.
        </p>
        <ul className="text-sm text-gray-700 space-y-0.5">
          <li><b>Basic</b> — two feeds, a pre-emergent and a weed treatment.</li>
          <li><b>Plus aeration</b> — the same, plus a winterizing feed, a core aeration and an insect control.</li>
        </ul>
        <p className="text-[11px] text-gray-500">
          Everything still up goes down on the first visit. The winterizing feed follows a month later. The last feed,
          the coring and the insect control land in the final month. <b>Billing stops when the plan ends</b> — nobody
          pays past {data?.end_month}. Nothing is texted and nothing is charged.
        </p>
      </div>

      <SplitWeek />
      <PushBack />

      {done && (
        <div className="card p-4 border border-emerald-300 bg-emerald-50/60">
          <p className="text-sm font-semibold text-emerald-900">{done.count} plan{done.count === 1 ? '' : 's'} will finish and end {done.ends_on}.</p>
          <ul className="mt-1 text-xs text-emerald-900/80 space-y-0.5">
            {(done.done || []).map((c: any) => (
              <li key={c.program_id}>{c.customer} — {c.booked} visit{c.booked === 1 ? '' : 's'} booked, last one {day(c.last_visit)}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        {people.map(p => {
          const k = pick(p.program_id);
          return (
            <div key={p.program_id} className="card px-4 py-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-gray-900">{p.customer}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.grass} · {Number(p.sqft || 0).toLocaleString()} sq ft · pays {money(p.pays_now)} a month
                    {p.address ? ` · ${p.address}` : ''}
                    {p.ends_on ? ` · already ends ${p.ends_on}` : ''}
                  </p>
                  {/* Say which ones were set from his list of names, so a wrong
                      match is visible before anything is booked. */}
                  {p.suggested?.matched_on && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      On Basic because you named {p.suggested.matched_on}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {CHOICES.map(c => (
                    <button key={c.key} type="button" onClick={() => setKinds(s => ({ ...s, [String(p.program_id)]: c.key }))}
                      className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold ${k === c.key ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-300'}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {k === 'skip' ? (
                <p className="mt-1.5 text-xs text-gray-500">Left as it is — their plan keeps running.</p>
              ) : (
                <ul className="mt-2 text-xs text-gray-800 space-y-0.5">
                  {(p.visits || []).map((v: any) => (
                    <li key={v.month} className={v.no_room ? 'text-amber-700' : ''}>
                      <b>{day(v.date)}</b> — {v.words}
                      <span className="text-gray-500">{v.rides ? ' · on the mow' : v.no_room ? ' · no mow that month yet, it will hop on when one is booked' : ' · a trip of its own'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {(data?.order || []).length > 0 && (
        <div className="card p-4">
          <p className="text-sm font-semibold text-gray-900">What to order</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Everything these visits need, in whole bags and bottles with waste already in.</p>
          <ul className="mt-2 divide-y divide-gray-100">
            {(data.order || []).map((o: any) => (
              <li key={o.key} className="py-1.5 flex items-baseline justify-between gap-3 text-sm">
                <span className="text-gray-900">
                  <b>{o.packs} × {o.pack}</b> — {o.name}
                  <span className="text-gray-500"> · {o.word} · {o.amount.toLocaleString()} {o.unit} needed</span>
                </span>
                <span className="text-gray-600 flex-shrink-0">{money(o.cost)}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm font-semibold text-gray-900 mt-2">{money(data.order_cost)} to buy</p>
        </div>
      )}

      <div className="card p-4 flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => finish.mutate()} disabled={finish.isLoading || !taking.length}
          className="btn-primary text-sm disabled:opacity-50">
          {finish.isLoading ? 'Working…' : `Finish ${taking.length} and end ${data?.end_month}`}
        </button>
        <p className="text-xs text-gray-500">
          {data?.totals?.visits ? `${data.totals.visits} visits owed across everyone. ` : ''}
          Visits already done or started are never touched. Anything booked beyond the end comes off.
        </p>
      </div>
    </div>
  );
}

/**
 * The bags slide to next week; the spray stays.
 *
 * The owner, Sep 28 2026: "move any fertilizing and pre emergent to next week,
 * keep weed sprays with the clients this week. Tell me what I need to order."
 * A visit carrying both is split — the spray keeps its day and a second visit
 * takes the bags to the yard's next mow. What to buy for the moved visits comes
 * back with the answer, in whole bags off the cost sheet.
 */
const dayShort = (iso?: string | null) =>
  (iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const bags = (l: any) => (l.unit === 'visit' || l.unit === 'each' ? `${l.packs} ${l.word}` : `${l.packs} × ${l.pack}`);

function SplitWeek() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'next_mow' | 'days'>('next_mow');
  const [look, setLook] = useState<any>(null);

  const go = useMutation((dryRun: boolean) => api.post('/treatments/split-week', { dry_run: dryRun, to: mode, days: 7 }).then(r => r.data), {
    onSuccess: (d: any) => {
      setLook(d);
      if (!d.dry_run) {
        toast.success(d?.message || 'Moved', { autoClose: 9000 });
        qc.invalidateQueries('plan-finish'); qc.invalidateQueries('treatment-programs'); qc.invalidateQueries('plan-buy');
      }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not move those'); },
  });

  const rows: any[] = look?.rows || [];
  const touched = rows.filter(r => r.go?.length);
  const order = look?.order;

  return (
    <div className="card p-4 space-y-2">
      <p className="text-sm font-semibold text-gray-900">Bags to next week, sprays stay</p>
      <p className="text-xs text-gray-600">
        Every plan visit this week that carries <b>fertilizer or pre-emergent</b> hands those to next week. A visit that also
        has a weed treatment (or anything else) is split — the spray keeps its day, a second visit takes the bags.
      </p>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <span className="text-gray-500">The bags go to</span>
        <button type="button" onClick={() => { setMode('next_mow'); setLook(null); }}
          className={`px-2.5 py-1.5 rounded-lg border font-semibold ${mode === 'next_mow' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
          their next mow after this week
        </button>
        <button type="button" onClick={() => { setMode('days'); setLook(null); }}
          className={`px-2.5 py-1.5 rounded-lg border font-semibold ${mode === 'days' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
          the same day next week
        </button>
      </div>

      {look && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5 text-xs text-gray-800 space-y-1 max-h-72 overflow-auto">
          <p className="font-semibold">{look.dry_run ? 'This is what it would do:' : look.message}</p>
          {touched.map((r: any) => (
            <p key={r.round_id}>
              <b>{r.customer}</b> — {r.go_words} · {dayShort(r.date)} → <b>{dayShort(r.to)}</b>
              {r.on_a_mow ? ' (on their mow)' : ' (its own trip — no mow that day)'}
              {r.beyond_next_week ? ' — that is past next week' : ''}
              {r.joins ? <span> · joins the {r.joins.was.toLowerCase()} already booked that day{r.joins.already?.length ? ` (${r.joins.already_words.toLowerCase()} is on it once, not twice)` : ''}</span> : ''}
              {r.split ? <span className="text-emerald-800"> · {r.stay_words} stays {dayShort(r.date)}</span> : ''}
            </p>
          ))}
          {rows.filter(r => !r.go?.length).map((r: any) => (
            <p key={r.round_id} className="text-gray-500">{r.customer} — {r.stay_words || r.keys?.join(' + ')} · stays {dayShort(r.date)}</p>
          ))}
          {!rows.length && <p className="text-gray-500">No plan visits this week.</p>}
          {order && (order.moving?.length > 0 || order.staying?.length > 0) && (
            <div className="pt-1.5 mt-1.5 border-t border-gray-200 space-y-0.5">
              {order.moving?.length > 0 && (
                <p><b>To order for the bags{look.dry_run ? '' : ' (now next week)'}:</b> {order.moving.map((l: any) => `${bags(l)} — ${l.name}`).join(' · ')} · <b>{money(order.moving_cost)}</b></p>
              )}
              {order.staying?.length > 0 && (
                <p><b>Still going down this week, as planned:</b> {order.staying.map((l: any) => `${bags(l)} — ${l.name}`).join(' · ')}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => go.mutate(true)} disabled={go.isLoading}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-50">
          {go.isLoading ? 'Working…' : 'Look first'}
        </button>
        {look?.dry_run && touched.length > 0 && (
          <button type="button" onClick={() => go.mutate(false)} disabled={go.isLoading}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
            Move the bags on {touched.length} visit{touched.length === 1 ? '' : 's'}
          </button>
        )}
        <span className="text-[11px] text-gray-500">Visits already done or started are never touched. Nothing is texted or charged.</span>
      </div>
    </div>
  );
}

/**
 * Slide treatment visits later.
 *
 * The owner, Sep 21 2026: "any treatment plans happening this week move back at
 * least a week," then "also move back all the visits a week." Weather, or a
 * week that is already full — either way the whole lot slides rather than being
 * worked round one customer at a time.
 *
 * Visits done or started are never touched, and everything moved is pinned so
 * nothing quietly books it back where it was.
 */
function PushBack() {
  const qc = useQueryClient();
  const [scope, setScope] = useState<'week' | 'all'>('week');
  const [days, setDays] = useState(7);
  // A treatment rides a mow, so "seven days" lands it on a day nobody is going
  // to that yard. Their next mow is the honest place for it (owner, Sep 21
  // 2026: "instead of pushing visits on treatments 7 days, push visits to next
  // visit").
  const [mode, setMode] = useState<'next_mow' | 'days'>('next_mow');
  const [look, setLook] = useState<any>(null);

  const body = (dryRun: boolean) => ({
    dry_run: dryRun, days, to: mode,
    ...(scope === 'all' ? { through: '2099-12-31' } : {}),
  });
  const push = useMutation((dryRun: boolean) => api.post('/treatments/push-week', body(dryRun)).then(r => r.data), {
    onSuccess: (d: any) => {
      setLook(d);
      if (!d.dry_run) {
        toast.success(d?.message || 'Moved', { autoClose: 9000 });
        qc.invalidateQueries('plan-finish'); qc.invalidateQueries('treatment-programs');
      }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not move those'); },
  });

  return (
    <div className="card p-4 space-y-2">
      <p className="text-sm font-semibold text-gray-900">Push the visits back</p>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        {([['week', 'This week'], ['all', 'Everything ahead']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => { setScope(k); setLook(null); }}
            className={`px-2.5 py-1.5 rounded-lg border font-semibold ${scope === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
            {label}
          </button>
        ))}
        <span className="text-gray-500 ml-1">to</span>
        <button type="button" onClick={() => { setMode('next_mow'); setLook(null); }}
          className={`px-2.5 py-1.5 rounded-lg border font-semibold ${mode === 'next_mow' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
          their next mow
        </button>
        {[7, 14].map(n => (
          <button key={n} type="button" onClick={() => { setMode('days'); setDays(n); setLook(null); }}
            className={`px-2.5 py-1.5 rounded-lg border font-semibold ${mode === 'days' && days === n ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
            +{n} days
          </button>
        ))}
      </div>

      {look && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5 text-xs text-gray-800 space-y-0.5 max-h-52 overflow-auto">
          <p className="font-semibold">{look.dry_run ? 'This is what it would do:' : look.message}</p>
          {(look.moved || []).map((m: any, i: number) => (
            <p key={i}>{m.customer} — {m.what} · {m.from} → {m.to}{m.on_a_mow ? ' (on their mow)' : ''}</p>
          ))}
          {!(look.moved || []).length && <p className="text-gray-500">Nothing to move.</p>}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => push.mutate(true)} disabled={push.isLoading}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-50">
          {push.isLoading ? 'Working…' : 'Look first'}
        </button>
        {look?.dry_run && (look.moved || []).length > 0 && (
          <button type="button" onClick={() => push.mutate(false)} disabled={push.isLoading}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
            Move those {look.moved.length} {mode === 'next_mow' ? 'to their next mow' : `back ${days} days`}
          </button>
        )}
        <span className="text-[11px] text-gray-500">Visits already done or started are never touched. Nothing is texted or charged.</span>
      </div>
    </div>
  );
}
